
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Modal from 'react-modal'; // Import Modal
import DataReviewSidebar from './DataReviewSidebar';
import CombinedNetworkGraph from './CombinedNetworkGraph';
import ColumnSelectionModal from './ColumnSelectionModal';
import Markdown from 'markdown-to-jsx';
import './App.css';
import './DataReview.css';
// Permission gating component
import ProtectedElement from './ProtectedElement';

// Ensure Modal is configured
Modal.setAppElement('#root'); // Or your app's root element ID

// Legend component (Unchanged)
const Legend = ({ legendData }) => (
    <div className="legend-container data-review-legend constrained-legend">
      <h4 style={{width: '100%', marginTop: 0, marginBottom: '8px', flexShrink: 0 }}>Graph Legend</h4>
      <div className="legend-items">
        {Object.entries(legendData).map(([fileName, color]) => ( <div key={fileName} style={{ display: 'flex', alignItems: 'center', marginRight: '15px', marginBottom: '5px' }}> <div style={{ width: '15px', height: '15px', backgroundColor: color, marginRight: '5px', border: '1px solid #ccc', flexShrink: 0 }} /> <span style={{fontSize: '0.9em'}}>{fileName}</span> </div> ))}
      </div>
    </div>
);

// --- Graph Pop-out Modal Styles (Unchanged) ---
const graphModalStyles = {
    content: {
        top: '50%', left: '50%', right: 'auto', bottom: 'auto',
        marginRight: '-50%', transform: 'translate(-50%, -50%)',
        width: '90vw', // 90% of viewport width
        height: '90vh', // 90% of viewport height
        padding: '15px', // Reduced padding
        border: '1px solid #ccc', borderRadius: '8px',
        boxShadow: '0 5px 20px rgba(0, 0, 0, 0.25)',
        backgroundColor: '#fff',
        zIndex: 1050, // Ensure it's above other elements if needed
        display: 'flex', // Use flexbox for layout inside
        flexDirection: 'column',
        overflow: 'hidden', // Prevent modal scroll, internal elements will scroll
    },
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.7)', // Darker overlay
        zIndex: 1040,
    },
};

const FileDetailModal = ({ isOpen, onClose, fileData, allColumns = [] }) => {
    const [activeTab, setActiveTab] = useState('details');
    const [selectedKnColumn, setSelectedKnColumn] = useState('');
    const [statements, setStatements] = useState([]);
    const [knColumns, setKnColumns] = useState([]);
    const [parsingError, setParsingError] = useState('');

    useEffect(() => {
        if (fileData) {
            const availableKnCols = allColumns.filter(col => col.startsWith('kn_') && fileData[col] != null);
            setKnColumns(availableKnCols);
            setSelectedKnColumn(availableKnCols.length > 0 ? availableKnCols[0] : '');
            setActiveTab('details');
            setParsingError('');
        }
    }, [fileData, allColumns]);

    const generateStatementsFromKnData = useCallback((knDataRaw) => {
        setParsingError('');
        if (!knDataRaw) return [];
        let jsonData = null;
        try {
            jsonData = typeof knDataRaw === 'string' ? JSON.parse(knDataRaw) : knDataRaw;
        } catch (error) {
            setParsingError(`Error parsing data in '${selectedKnColumn}'. Invalid JSON.`);
            return [];
        }
        if (!jsonData) return [];
        
        const dataArray = Array.isArray(jsonData) ? jsonData : [jsonData];
        const generatedStatements = [];
        dataArray.forEach((item, index) => {
            if (item && item.node_1 && item.node_2 && item.edge) {
                generatedStatements.push({ id: `${selectedKnColumn}-${index}`, text: `${item.node_1} ${item.edge} ${item.node_2}` });
            }
        });
        return generatedStatements;
    }, [selectedKnColumn]);

    useEffect(() => {
        if (fileData && selectedKnColumn) {
            setStatements(generateStatementsFromKnData(fileData[selectedKnColumn]));
        } else {
            setStatements([]);
        }
    }, [fileData, selectedKnColumn, generateStatementsFromKnData]);

    if (!fileData) return null;

    const detailColumns = allColumns.filter(col => !col.startsWith('kn_'));

    return (
        <Modal isOpen={isOpen} onRequestClose={onClose} contentLabel="File Details" className="dataview-modal-content file-detail-modal" overlayClassName="modal-overlay" ariaHideApp={false} >
            <div className="modal-header">
                <span>Details for: {fileData.file_name || `ID: ${fileData.id}`}</span>
                <button onClick={onClose} className="modal-close-button">Close</button>
            </div>
            <div className="modal-tabs">
                <button className={activeTab === 'details' ? 'active' : ''} onClick={() => setActiveTab('details')}>Details</button>
                {knColumns.length > 0 && ( <button className={activeTab === 'statements' ? 'active' : ''} onClick={() => setActiveTab('statements')}>Statements</button> )}
            </div>
            <div className="modal-content">
                {activeTab === 'details' && (
                    <div className="details-grid">
                        {detailColumns.map(col => (
                            <React.Fragment key={col}>
                                <div className="detail-label">{col}:</div>
                                <div className="detail-value">
                                    {typeof fileData[col] === 'object' && fileData[col] !== null ? ( <pre>{JSON.stringify(fileData[col], null, 2)}</pre> ) : ( <div>{String(fileData[col] ?? 'N/A')}</div> )}
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                )}
                {activeTab === 'statements' && (
                    <div className="statements-tab">
                        {knColumns.length > 0 ? (
                            <>
                                <div className="source-select-container">
                                    <label htmlFor="knColumnSelect">Select Source:</label>
                                    <select id="knColumnSelect" value={selectedKnColumn} onChange={(e) => setSelectedKnColumn(e.target.value)}>
                                        {knColumns.map(col => <option key={col} value={col}>{col}</option>)}
                                    </select>
                                </div>
                                {parsingError && <div className="error">{parsingError}</div>}
                                {statements.length > 0 ? (
                                    <ol className="statement-list">
                                        {statements.map((stmt, index) => <li key={stmt.id || index} className="statement-item">{stmt.text}</li>)}
                                    </ol>
                                ) : ( !parsingError && <p>No statements found for '{selectedKnColumn}'.</p> )}
                                <p className="statement-count">Displaying {statements.length} statements from: <strong>{selectedKnColumn}</strong></p>
                            </>
                        ) : ( <p>No knowledge columns (starting with 'kn_') found.</p> )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

const DataReview = ({ handleLogout }) => {
  // State variables remain the same...
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [collectionName, setCollectionName] = useState('');
  const [collectionDescription, setCollectionDescription] = useState('');
  const [knGraphOption, setKnGraphOption] = useState('kn_graph_full');
  const [filesData, setFilesData] = useState([]);
  const [graphDataSets, setGraphDataSets] = useState([]);
  const [legendData, setLegendData] = useState({});
  const [graphQuery, setGraphQuery] = useState('');
  const [statements, setStatements] = useState([]);
  const [statementFilter, setStatementFilter] = useState('');
  const [visibleColumns, setVisibleColumns] = useState(['id', 'file_name', 'key_concepts']);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false); // Renamed for clarity
  const [summary, setSummary] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingCollection, setIsSavingCollection] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeSettingsModalTab, setActiveSettingsModalTab] = useState('Select Columns'); // Renamed for clarity
  const [systemPrompts, setSystemPrompts] = useState([]);
  const [selectedSystemPrompt, setSelectedSystemPrompt] = useState('');
  const [activeInfoTab, setActiveInfoTab] = useState('statements');
  const [isStatementsVisible, setIsStatementsVisible] = useState(true);
  const [isFilesTableVisible, setIsFilesTableVisible] = useState(true);
  const [isGraphModalOpen, setIsGraphModalOpen] = useState(false);
  // --- *** NEW ***: State for File Detail Modal ---
  const [selectedFileForDetail, setSelectedFileForDetail] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  // --- End NEW State ---

  const baseURL = '';

  // Toggle functions remain the same
  const toggleStatementsVisibility = () => setIsStatementsVisible(prev => !prev);
  const toggleFilesTableVisibility = () => setIsFilesTableVisible(prev => !prev);

  // Graph Modal Handlers (Unchanged)
  const openGraphModal = () => setIsGraphModalOpen(true);
  const closeGraphModal = () => setIsGraphModalOpen(false);

  // --- *** NEW ***: File Detail Modal Handlers ---
   const handleFileRowClick = (file) => {
     console.log("File selected for details in DataReview:", file.id);
     setSelectedFileForDetail(file);
     setIsDetailModalOpen(true);
   };

   const closeDetailModal = () => {
     setIsDetailModalOpen(false);
     // Optional: Clear selection when closing
     // setSelectedFileForDetail(null);
   };
  // --- End NEW Handlers ---


  // Fetch initial data (remains the same)
  useEffect(() => {
    const fetchData = async () => {
        setIsLoading(true); setError(null);
        try {
            const [collectionsRes, promptsRes] = await Promise.all([
                axios.get(`${baseURL}/api/files/get_collection_names`, { withCredentials: true }),
                axios.get(`${baseURL}/api/prompts`, { withCredentials: true }).catch(err => { console.warn("Prompts API failed:", err); return { data: [] }; })
            ]);
            setCollections(collectionsRes.data || []);
            setSystemPrompts(promptsRes.data || []);
        } catch (err) {
            console.error('Error fetching initial data:', err); setError(`Failed to fetch initial data: ${err.response?.data?.error || err.message}`); if (err.response?.status === 401) { if (handleLogout) handleLogout(false); }
        } finally { setIsLoading(false); }
    };
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // handleLogout dependency removed as it doesn't change

  // Update available columns (remains the same)
   useEffect(() => {
    if (filesData.length > 0 && filesData[0]) {
      const columns = Object.keys(filesData[0]);
      setAvailableColumns(columns); // Store all available columns

      // Define default desired visible columns
      const defaultVisible = ['id', 'file_name', 'directory', 'topic_title', 'key_concepts'];
      // Filter defaults based on what actually exists in the data
      const newVisible = defaultVisible.filter(column => columns.includes(column));

      // Initialize or update visibleColumns state
      setVisibleColumns(prev => {
          // If currently visible columns are empty OR none of them exist in the new columns, reset.
           if (prev.length === 0 || !prev.some(vc => columns.includes(vc))) {
               return newVisible.length > 0 ? newVisible : columns.slice(0, 5); // Reset to defaults or first 5
           } else {
               // Otherwise, filter the current visible columns to only include those that still exist
               return prev.filter(col => columns.includes(col));
           }
      });
    } else {
      setAvailableColumns([]); // Reset if no data
      setVisibleColumns([]); // Also reset visible columns
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesData]); // Re-run when filesData changes


  // API Call Helper (remains the same)
  const makeApiCall = useCallback(async (method, url, data = null) => {
    try { const config = { method, url: `${baseURL}${url}`, withCredentials: true }; if (data) config.data = data; const response = await axios(config); return response.data; }
    catch (err) { console.error(`Error ${method} ${url}:`, err); const errorPayload = { message: `API Error (${url}): ${err.response?.data?.error || err.message}`, status: err.response?.status, originalError: err }; if (err.response?.status === 401) { if (handleLogout) handleLogout(false); } throw errorPayload; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseURL]); // handleLogout dependency removed

  // Fetch Summary (remains the same)
  const fetchSummary = async () => {
    if (filesData.length === 0) { setError("No files selected to generate a summary."); return; } setSummary(''); setIsLoading(true); setError(null); setSuccessMessage(null);
    const jsonDataToSummarize = filesData.map(file => { try { const graphContent = file[knGraphOption]; if (typeof graphContent === 'string') { try { return JSON.parse(graphContent); } catch { return null; } } else if (typeof graphContent === 'object' && graphContent !== null) { return graphContent; } return null; } catch (err) { return null; } }).filter(Boolean);
    if (jsonDataToSummarize.length === 0) { setError("No valid graph data found to summarize."); setIsLoading(false); return; } const systemPromptContent = selectedSystemPrompt || "Act as a Natural Language Expert summarizing key themes from structured data (Node-Edge-Node triples). Focus on high-level concepts and relationships. Keep it concise."; const payload = { messages: [{ role: "system", content: systemPromptContent }, { role: "user", content: JSON.stringify(jsonDataToSummarize) }] };
    try { 
        const response = await axios.post(`${baseURL}/api/prochat`, payload, { withCredentials: true });
        let summaryContent = ''; 
        if (response.data) { 
            summaryContent = response.data.summary || response.data.content || response.data;
        } 
        if (summaryContent) { 
            setSummary(summaryContent); 
        } else { 
            setError("Failed to get summary content from response."); 
            console.warn("Unexpected summary response structure:", response.data); 
        } 
    }
    catch (err) { 
        console.error('Summary API Error:', err); 
        setError(`Summary API Error: ${err.response?.data?.error || err.message}`); 
        if (err.response?.status === 401) { 
            if (handleLogout) handleLogout(false); 
        }
     } 
     finally { setIsLoading(false); }
  };

  // Generate statements (remains the same)
  const generateStatements = useCallback((data) => { 
    let allStatements = []; 
    data.forEach(fileData => {
        try { const graphContent = fileData[knGraphOption]; 
            let jsonData = null; 
            if (graphContent && typeof graphContent === 'string') { 
                try { 
                    jsonData = JSON.parse(graphContent); 
                } 
                catch { jsonData = null; } 
            } else if (graphContent && typeof graphContent === 'object') { 
                jsonData = graphContent; 
            } 
            if (Array.isArray(jsonData)) { 
                const sourceFileName = fileData.file_name || `FileID:${fileData.id}`; 
                jsonData.forEach(item => { if (item && item.node_1 && item.node_2 && item.edge) { 
                    const n1 = String(item.node_1).trim(); 
                    const n2 = String(item.node_2).trim(); 
                    const edge = String(item.edge).trim(); 
                    if (n1 && n2 && edge) { 
                        allStatements.push({ text: `${n1} ${edge} ${n2}`, source: sourceFileName }); 
                    } } }); 
                } else if(jsonData && typeof jsonData === 'object') { 
                    /* Handle single object if needed */ 
                    const sourceFileName = fileData.file_name || `FileID:${fileData.id}`; 
                    if (jsonData.node_1 && jsonData.node_2 && jsonData.edge) { 
                        const n1 = String(jsonData.node_1).trim(); 
                        const n2 = String(jsonData.node_2).trim(); 
                        const edge = String(jsonData.edge).trim(); 
                        if (n1 && n2 && edge) { 
                            allStatements.push({ text: `${n1} ${edge} ${n2}`, source: sourceFileName }); 
                        } 
                    } 
                } 
            } catch (error) { 
                console.error(`Error processing file ${fileData.id} for statements`, error); 
            } 
        }); 
        setStatements(allStatements); 
    }, [knGraphOption]);

  // Add Files To List (remains the same)
  const addFilesToList = useCallback((filesToAdd) => { if (!Array.isArray(filesToAdd)) { console.error("addFilesToList expects an array."); return; } setFilesData((prevFiles) => { const existingIds = new Set(prevFiles.map(f => f.id)); const newFiles = filesToAdd.filter(file => file && file.id && !existingIds.has(file.id)); if (newFiles.length === 0) return prevFiles; return [...prevFiles, ...newFiles]; }); setSelectedCollection(''); }, []);

  // Reset Graph Data (remains the same)
  const resetGraphData = useCallback(() => { setGraphDataSets([]); setLegendData({}); }, []);

  // Clear Files (remains the same)
  const handleClearFiles = useCallback(() => { setFilesData([]); setSelectedCollection(''); resetGraphData(); setSummary(''); setStatements([]); setStatementFilter(''); setError(null); setSuccessMessage(null); setGraphQuery(''); }, [resetGraphData]);

    // Delete Collection
    const handleDeleteCollection = useCallback(async () => {
        if (!selectedCollection) {
            setError('No collection selected to delete.');
            setSuccessMessage(null);
            return;
        }
        const confirmed = window.confirm(`Delete collection '${selectedCollection}'? This cannot be undone.`);
        if (!confirmed) return;

        setIsLoading(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const res = await makeApiCall('delete', `/api/files/delete-collection/${encodeURIComponent(selectedCollection)}`);
            setSuccessMessage(res?.message || `Collection '${selectedCollection}' deleted.`);
            // Refresh collections list
            const collectionsRes = await makeApiCall('get', '/api/files/get_collection_names');
            if (collectionsRes) setCollections(collectionsRes);
            // Clear current selection and related state
            setSelectedCollection('');
            setFilesData([]);
            resetGraphData();
            setSummary('');
            setStatements([]);
            setStatementFilter('');
        } catch (err) {
            setError(err.message || `Failed to delete collection '${selectedCollection}'.`);
        } finally {
            setIsLoading(false);
            setTimeout(() => setSuccessMessage(null), 3000);
        }
    }, [selectedCollection, makeApiCall, resetGraphData]);

  // Handle Collection Change (remains the same)
  const handleCollectionChange = useCallback(async (collectionName) => { 
    setSelectedCollection(collectionName); 
    setError(null); 
    setSuccessMessage(null); 
    resetGraphData(); 
    setSummary(''); 
    setStatements([]); 
    setStatementFilter(''); 
    setGraphQuery(''); 
    if (!collectionName) { 
        handleClearFiles(); 
        return; 
    } setIsLoading(true); 
    try { 
        const fileIds = await makeApiCall('get', `/api/files/get_collection_file_ids/${collectionName}`); 
        if (!Array.isArray(fileIds)) { 
            throw new Error(`Invalid file ID list format for collection '${collectionName}'.`); 
        } 
        if (fileIds.length === 0) { 
            setFilesData([]); 
            setSuccessMessage(`Collection '${collectionName}' loaded (0 files).`); 
        } else { 
            const fetchedFilesData = await makeApiCall('post', '/api/files/get_files_by_ids', { selectedIds: fileIds }); 
            if (!Array.isArray(fetchedFilesData)) { 
                throw new Error(`Invalid file details format for collection '${collectionName}'.`); 
            } 
            setFilesData(fetchedFilesData); 
            setSuccessMessage(`Collection '${collectionName}' loaded (${fetchedFilesData.length} files).`); 
        } 
        setTimeout(() => setSuccessMessage(null), 3000); 
    } 
    catch (err) { 
        console.error(`Error loading collection ${collectionName}:`, err); 
        setError(err.message || `Failed to load collection '${collectionName}'.`); 
        setFilesData([]); 
    } finally { setIsLoading(false); } 
}, [makeApiCall, handleClearFiles, resetGraphData]);

  // Generate statements effect (remains the same)
  useEffect(() => { 
    if (filesData.length > 0) { 
        generateStatements(filesData); 
    } else { 
        setStatements([]); 
    } 
    }, [filesData, knGraphOption, generateStatements]);

  // Create Collection (remains the same)
  const handleCreateCollection = async () => { if (!collectionName.trim()) { setError('Collection Name required.'); setSuccessMessage(null); return; } if (filesData.length === 0) { setError('No files selected.'); setSuccessMessage(null); return; } const fileIds = filesData.map(file => file.id); setIsSavingCollection(true); setError(null); setSuccessMessage(null); try { const result = await makeApiCall('post', '/api/files/create-collection', { name: collectionName.trim(), description: collectionDescription.trim(), files: fileIds || [] }); setSuccessMessage(result.message || `Collection '${collectionName.trim()}' created.`); setTimeout(() => setSuccessMessage(null), 4000); setCollectionName(''); setCollectionDescription(''); const collectionsRes = await makeApiCall('get', '/api/files/get_collection_names'); if (collectionsRes) setCollections(collectionsRes); } catch (err) { setError(err.message || 'Failed to create collection.'); } finally { setIsSavingCollection(false); } };

  // Create Knowledge Graph (remains the same)
    const handleCreateKnowledgeGraph = useCallback(() => {
        if (filesData.length === 0) {
            setError("No files selected to create graph.");
            resetGraphData();
            return;
        }

        setError(null);
        setSuccessMessage(null);
        resetGraphData();

        const newLegendData = {};
        const newGraphDataSets = [];
        let fileErrors = 0;

        filesData.forEach((file, index) => {
            try {
                const graphContent = file[knGraphOption];
                let rawData = null;

                if (!graphContent) {
                    return;
                }

                if (typeof graphContent === 'string') {
                    try {
                        rawData = JSON.parse(graphContent);
                    } catch {
                        throw new Error(`Invalid JSON`);
                    }
                } else if (typeof graphContent === 'object' && graphContent !== null) {
                    rawData = graphContent;
                } else {
                    return;
                }

                let dataArray = Array.isArray(rawData)
                    ? rawData
                    : (typeof rawData === 'object' && rawData !== null ? [rawData] : []);

                const queryResults = graphQuery
                    ? refineGraphData(dataArray, graphQuery)
                    : dataArray;

                if (queryResults && Array.isArray(queryResults) && queryResults.length > 0) {
                    const color = `hsl(${index * 137.508 % 360}, 65%, 55%)`;
                    const fileName = file.file_name || `File ${file.id}`;
                    newLegendData[fileName] = color;

                    const structuredData = structureGraphData(queryResults);

                    if (structuredData.nodes.length > 0 || structuredData.edges.length > 0) {
                        newGraphDataSets.push({
                            nodesData: structuredData.nodes.map(node => ({ ...node, color })),
                            edgesData: structuredData.edges,
                            fileName: fileName
                        });
                    }
                }
            } catch (error) {
                setError(prev => `${prev ? prev + '; ' : ''}Error processing ${file.file_name || file.id}.`);
                fileErrors++;
            }
        });

        if (newGraphDataSets.length === 0 && fileErrors === 0 && !error) {
            setError(`No graph data generated${graphQuery ? ' matching filter' : ''}.`);
        }

        setLegendData(newLegendData);
        setGraphDataSets(newGraphDataSets);
    }, [filesData, knGraphOption, graphQuery, resetGraphData, error]);

  // Structure Graph Data (remains the same)
  const structureGraphData = (jsonData) => { const nodes = new Map(); const edges = []; if (!Array.isArray(jsonData)) { return { nodes: [], edges: [] }; } jsonData.forEach((item, idx) => { if (!item || typeof item !== 'object' || !item.node_1 || !item.node_2 || typeof item.node_1 !== 'string' || typeof item.node_2 !== 'string') { return; } const node1Trimmed = item.node_1.trim(); const node2Trimmed = item.node_2.trim(); const edgeLabel = String(item.edge || '').trim(); if (!node1Trimmed || !node2Trimmed) { return; } if (!nodes.has(node1Trimmed)) nodes.set(node1Trimmed, { id: node1Trimmed, label: node1Trimmed }); if (!nodes.has(node2Trimmed)) nodes.set(node2Trimmed, { id: node2Trimmed, label: node2Trimmed }); edges.push({ id: `${node1Trimmed}-${edgeLabel}-${node2Trimmed}-${idx}`, from: node1Trimmed, to: node2Trimmed, label: edgeLabel, title: `${node1Trimmed} -(${edgeLabel})-> ${node2Trimmed}` }); }); return { nodes: Array.from(nodes.values()), edges }; };

  // Filter Graph Data (remains the same)
  const refineGraphData = (jsonData, query) => { if (!Array.isArray(jsonData)) { return []; } const queries = query.toLowerCase().split(',').map(q => q.trim()).filter(Boolean); if (queries.length === 0) return jsonData; return jsonData.filter(item => { if (!item || typeof item !== 'object') return false; const node1Lower = String(item.node_1 || '').toLowerCase(); const node2Lower = String(item.node_2 || '').toLowerCase(); const edgeLower = String(item.edge || '').toLowerCase(); return queries.some(q => node1Lower.includes(q) || node2Lower.includes(q) || edgeLower.includes(q) ); }); };

  // Settings Modal handlers (Renamed state/handlers)
  const handleSettingsModalOpen = () => setIsSettingsModalOpen(true);
  const handleSettingsModalClose = () => setIsSettingsModalOpen(false);
  const handleColumnToggle = (column) => { setVisibleColumns(currentColumns => currentColumns.includes(column) ? currentColumns.filter(col => col !== column) : [...currentColumns, column] ); };

  // Render Statements Tab Content (remains the same)
  const renderStatementsTabContent = () => { 
    if (!isStatementsVisible || filesData.length === 0) return ( <div className="placeholder-info data-review-section" style={{border: 'none', padding: '20px 0'}}> {filesData.length === 0 ? 'No files selected.' : 'Statements section is hidden via settings.'} </div> ); 
    const filteredStatements = statements.filter(stmtObj => statementFilter.trim() === '' || stmtObj.text.toLowerCase().includes(statementFilter.toLowerCase())); 
    return ( 
    <> 
    <div className="statement-controls"> 
        <input type="text" className="statement-filter-input" placeholder={`Filter ${statements.length} statements...`} 
        value={statementFilter} 
        onChange={(e) => setStatementFilter(e.target.value)} 
        disabled={isLoading || isSavingCollection} 
        /> 
        <span className="statement-source-info">(Source: '{knGraphOption}')</span> 
    </div> {statements.length === 0 && !isLoading && ( <p>No statements found using '{knGraphOption}'.</p> )} {statements.length > 0 && ( <ul className="statement-list"> {filteredStatements.length > 0 ? ( filteredStatements.map((stmtObj, index) => ( <li key={index} className="statement-item" title={`Source: ${stmtObj.source}`}> <span className="statement-text-content">{stmtObj.text}</span> <span className="statement-source-file">({stmtObj.source})</span> </li> )) ) : ( <li className="statement-item-empty">No statements match filter "{statementFilter}".</li> )} </ul> )} </> ); };

  // --- *** UPDATED ***: Render Selected Files Table Tab Content ---
  // Added onClick handler to table rows
  const renderFilesTableTabContent = () => {
      if (!isFilesTableVisible || filesData.length === 0) {
          return ( <div className="placeholder-info data-review-section" style={{border: 'none', padding: '20px 0'}}> {filesData.length === 0 ? 'No files selected.' : 'Files table is hidden via settings.'} </div> );
      }
       // Use availableColumns derived from useEffect
      const columnsToRender = visibleColumns.filter(vc => availableColumns.includes(vc));

      if (columnsToRender.length === 0 && availableColumns.length > 0) {
          return ( <p>No columns visible. Click 'Settings' to choose columns.</p> );
      }
      if (availableColumns.length === 0) {
          return ( <p>No data/columns found.</p> );
      }
      return (
          <div className="data-table-container data-review-table-container">
              <table className="data-review-table">
                   <thead>
                       <tr>
                           {/* Render based on filtered visibleColumns */}
                           {columnsToRender.map(column => <th key={column}>{column}</th>)}
                       </tr>
                   </thead>
                   <tbody>
                       {filesData.map(file => (
                           <tr
                               key={file.id}
                               onClick={() => handleFileRowClick(file)} // *** ADDED onClick ***
                               className={isDetailModalOpen && selectedFileForDetail?.id === file.id ? 'selected-row' : ''} // *** ADDED className for highlight ***
                               style={{ cursor: 'pointer' }} // *** ADDED style for cursor ***
                               title={`Click to view details for ${file.file_name || file.id}`} // *** ADDED title ***
                           >
                               {columnsToRender.map(column =>
                                   <td key={column} title={typeof file[column] === 'object' ? JSON.stringify(file[column]) : String(file[column] ?? '')}>
                                       {/* Truncated display remains the same */}
                                       {typeof file[column] === 'object' && file[column] !== null ? JSON.stringify(file[column]).substring(0, 100) + (JSON.stringify(file[column])?.length > 100 ? '...' : '') : String(file[column] ?? '').substring(0, 150) + (String(file[column] ?? '').length > 150 ? '...' : '') }
                                   </td>
                               )}
                           </tr>
                       ))}
                   </tbody>
              </table>
          </div>
      );
  }

  // Render Summary Tab Content (remains the same)
  const renderSummaryTabContent = () => { if (summary) { return ( <div className="summary-content-area"> <pre className='markdown-text'>{summary}</pre> </div> ); } else if (filesData.length > 0) { return ( <div className="placeholder-info data-review-section" style={{border: 'none', padding: '20px 0'}}> Click 'Generate Summary' for AI summary (using '{knGraphOption}'). </div> ); } else { return ( <div className="placeholder-info data-review-section" style={{border: 'none', padding: '20px 0'}}> Select files first to generate a summary. </div> ); } };


  // --- Component Return JSX ---
    return (
        <ProtectedElement
            required={["collections:read-names"]}
            fallbackVariant="overlay"
        >
        <div className="data-review-container">
      <DataReviewSidebar
        collections={collections} selectedCollection={selectedCollection} setSelectedCollection={handleCollectionChange}
        knGraphOption={knGraphOption} setKnGraphOption={setKnGraphOption}
        addFilesToList={addFilesToList} selectedFiles={filesData}
        isLoading={isLoading || isSavingCollection}
      />

      <div className="data-review-main-content">
        {/* Header and Top Controls (Unchanged) */}
        <h2>Data Review & Analysis</h2>
        <p>Select files (by collection or search) to analyze, visualize, and summarize.</p>
        {(isLoading || isSavingCollection) && <div className="loading">Loading / Saving...</div>}
        {error && <div className="error">{error}</div>}
        {successMessage && <div className="success">{successMessage}</div>}
                <div className="data-review-controls">
                         <div className="control-item filter-graph-input">
                             <label htmlFor="graphQueryInput">Filter Graph (Keywords):</label>
                             <input id="graphQueryInput" type="text" placeholder="e.g., concept1, relationship2" value={graphQuery} onChange={(e) => setGraphQuery(e.target.value)} disabled={isLoading || isSavingCollection} />
                         </div>
                         <div className="control-item action-buttons">
                             <ProtectedElement required={["collections:create"]} behavior="disable" disabledTooltip="Need data-review:visualize permission">
                                 <button onClick={handleCreateKnowledgeGraph} disabled={isLoading || isSavingCollection || filesData.length === 0}>Visualize Graph</button>
                             </ProtectedElement>
                             <ProtectedElement required={["collections:create"]} behavior="disable" disabledTooltip="Need data-review:summary permission">
                                 <button onClick={fetchSummary} disabled={isLoading || isSavingCollection || filesData.length === 0}>Generate Summary</button>
                             </ProtectedElement>
                             <button onClick={handleClearFiles} disabled={isLoading || isSavingCollection} className="clear-button">Clear Selection</button>
                             <button onClick={handleSettingsModalOpen} disabled={isLoading || isSavingCollection} className="settings-button">Settings</button>
                                                         {selectedCollection && (
                                                             <ProtectedElement required={["collections:delete"]} behavior="disable" disabledTooltip="Need collections:delete permission">
                                                                 <button onClick={handleDeleteCollection} disabled={isLoading || isSavingCollection} className="clear-button" title={`Delete collection '${selectedCollection}'`}>
                                                                     Delete Collection
                                                                 </button>
                                                             </ProtectedElement>
                                                         )}
                         </div>
                </div>
       <div className="collection-creation-controls">
           <input type="text" placeholder="New Collection Name" value={collectionName} onChange={(e) => setCollectionName(e.target.value)} disabled={isLoading || isSavingCollection} />
           <input type="text" placeholder="Description (Optional)" value={collectionDescription} onChange={(e) => setCollectionDescription(e.target.value)} disabled={isLoading || isSavingCollection} />
           <ProtectedElement required={["collections:create"]} behavior="disable" disabledTooltip="Need collections:create permission">
            <button onClick={handleCreateCollection} disabled={isLoading || isSavingCollection || filesData.length === 0 || !collectionName.trim()}> {isSavingCollection ? 'Saving...' : 'Save as Collection'} </button>
           </ProtectedElement>
       </div>

        {/* Main Results Area Grid Layout (Unchanged structure) */}
        <div className="data-review-results-area">
            {/* Top Row: Graph Area (Unchanged structure, button added) */}
            <div className="graph-display-area data-review-section">
                 <div className="graph-area-header">
                    <h3 style={{ marginTop: 0, marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>Knowledge Graph Visualization</h3>
                    {graphDataSets.length > 0 && (
                        <button onClick={openGraphModal} className="popout-button" title="Open graph in large view">
                            Pop Out Graph ↗
                        </button>
                     )}
                 </div>
                 {Object.keys(legendData).length > 0 && ( <Legend legendData={legendData} /> )}
                 {graphDataSets.length > 0 ? (
                     <div className="graph-visualization-wrapper"> <CombinedNetworkGraph fileDataSets={graphDataSets} /> </div>
                 ) : (
                     <div className="graph-placeholder"> {filesData.length > 0 ? `Click 'Visualize Graph' to view the graph for ${filesData.length} selected file(s). ${graphQuery ? ` (Filter: "${graphQuery}")` : ''}` : 'Select files via the sidebar to visualize the graph.'} </div>
                 )}
            </div>

             {/* Bottom Row: Tabs for Info (Unchanged structure, content render updated) */}
            <div className="data-review-info-tabs-container data-review-section">
                <div className="info-tabs">
                    <button className={`info-tab ${activeInfoTab === 'summary' ? 'active' : ''}`} onClick={() => setActiveInfoTab('summary')} disabled={isLoading || isSavingCollection}> Summary {summary ? '✓' : ''} </button>
                     {isStatementsVisible && ( <button className={`info-tab ${activeInfoTab === 'statements' ? 'active' : ''}`} onClick={() => setActiveInfoTab('statements')} disabled={isLoading || isSavingCollection}> Statements ({statements.length}) </button> )}
                     {isFilesTableVisible && ( <button className={`info-tab ${activeInfoTab === 'files' ? 'active' : ''}`} onClick={() => setActiveInfoTab('files')} disabled={isLoading || isSavingCollection}> Files Table ({filesData.length}) </button> )}
                </div>
                <div className="info-tab-content">
                    {activeInfoTab === 'summary' && renderSummaryTabContent()}
                    {activeInfoTab === 'statements' && isStatementsVisible && renderStatementsTabContent()}
                    {/* Renders the updated files table with clickable rows */}
                    {activeInfoTab === 'files' && isFilesTableVisible && renderFilesTableTabContent()}
                </div>
            </div>
        </div>

        {/* Settings Modal (Unchanged) */}
         {isSettingsModalOpen && (
             <ColumnSelectionModal
                isOpen={isSettingsModalOpen} onClose={handleSettingsModalClose}
                activeTab={activeSettingsModalTab} setActiveTab={setActiveSettingsModalTab}
                visibleColumns={visibleColumns} systemPrompts={systemPrompts}
                selectedSystemPrompt={selectedSystemPrompt} setSelectedSystemPrompt={setSelectedSystemPrompt}
                columns={availableColumns} onToggleColumn={handleColumnToggle}
                isStatementsVisible={isStatementsVisible} toggleStatementsVisibility={toggleStatementsVisibility}
                isFilesTableVisible={isFilesTableVisible} toggleFilesTableVisibility={toggleFilesTableVisibility}
             />
         )}

        {/* Graph Pop-out Modal (Unchanged) */}
         <Modal
            isOpen={isGraphModalOpen}
            onRequestClose={closeGraphModal}
            style={graphModalStyles}
            contentLabel="Graph Visualization Pop-out"
            ariaHideApp={false} // Added for accessibility best practice
         >
            {/* Graph modal content */}
             <div className="graph-modal-content">
                 <div className="graph-modal-header">
                     <h2>Graph Visualization (Pop-out)</h2>
                     <button onClick={closeGraphModal} className="modal-close-button simple">Close</button>
                 </div>
                 {Object.keys(legendData).length > 0 && (
                    <div className="graph-modal-legend">
                         <Legend legendData={legendData} />
                    </div>
                 )}
                 <div className="graph-modal-visualization">
                     {graphDataSets.length > 0 ? (
                         <CombinedNetworkGraph fileDataSets={graphDataSets} options={{ height: "100%", width: "100%" }} />
                     ) : (
                         <p>No graph data available.</p>
                     )}
                 </div>
            </div>
         </Modal>

         {/* --- *** NEW ***: Render the File Detail Modal --- */}
          <FileDetailModal
             isOpen={isDetailModalOpen}
             onClose={closeDetailModal}
             fileData={selectedFileForDetail}
             // Pass availableColumns determined by the useEffect hook
             allColumns={availableColumns}
          />
         {/* --- End NEW Modal Render --- */}

      </div> {/* End Main Content */}
        </div>
        </ProtectedElement>
  );
};

export default DataReview;